import { cn } from '@/lib/utils';
import { Play, Check, CheckCheck, Info } from 'lucide-react';
import type { Message } from '@/lib/types/messaging';

interface MessageBubbleProps {
  message: Message;
  onImageClick?: (imageUrl: string) => void;
  onAudioPlay?: (audioUrl: string) => void;
}

export function MessageBubble({ message, onImageClick, onAudioPlay }: MessageBubbleProps) {
  const isOwn = message.isOwn;
  const isSystem = message.type === 'system';

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  const formatAudioDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (isSystem) {
    return (
      <div className="flex justify-center my-4 mx-4">
        <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/50 border border-border/50 px-4 py-2 rounded-full shadow-sm">
          <Info className="size-3.5" />
          <span>{message.content}</span>
        </div>
      </div>
    );
  }

  const renderContent = () => {
    switch (message.type) {
      case 'text':
        return (
          <p
            className={cn(
              'text-sm whitespace-pre-wrap break-words leading-relaxed',
              isOwn ? 'text-white' : 'text-foreground'
            )}
          >
            {message.content}
          </p>
        );

      case 'image':
        return (
          <div className="space-y-2">
            <div className="relative group overflow-hidden rounded-xl">
              <img
                src={message.imageThumbnailUrl || message.imageUrl || ''}
                alt="Image"
                onClick={() => message.imageUrl && onImageClick?.(message.imageUrl)}
                className="max-w-[280px] max-h-[280px] object-cover cursor-pointer transition-all duration-200 group-hover:scale-[1.02]"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors pointer-events-none" />
            </div>
            {message.content && (
              <p
                className={cn(
                  'text-sm leading-relaxed',
                  isOwn ? 'text-white' : 'text-foreground'
                )}
              >
                {message.content}
              </p>
            )}
          </div>
        );

      case 'audio':
        return (
          <div className="flex items-center gap-3 min-w-[180px]">
            <button
              onClick={() => message.audioUrl && onAudioPlay?.(message.audioUrl)}
              className={cn(
                'size-10 rounded-full flex items-center justify-center transition-all duration-200 shadow-sm',
                isOwn
                  ? 'bg-white/20 hover:bg-white/30 text-white hover:scale-105'
                  : 'bg-primary hover:bg-primary/90 text-white hover:scale-105'
              )}
            >
              <Play className="size-4 ml-0.5" />
            </button>
            <div className="flex items-center gap-[3px] flex-1">
              {[...Array(12)].map((_, i) => (
                <div
                  key={i}
                  className={cn(
                    'w-[3px] rounded-full transition-all',
                    isOwn ? 'bg-white/50' : 'bg-muted-foreground/40'
                  )}
                  style={{ height: `${8 + Math.sin(i * 0.8) * 8 + Math.random() * 4}px` }}
                />
              ))}
            </div>
            <span
              className={cn(
                'text-xs font-medium tabular-nums',
                isOwn ? 'text-white/80' : 'text-muted-foreground'
              )}
            >
              {formatAudioDuration(message.audioDuration || 0)}
            </span>
          </div>
        );

      default:
        return null;
    }
  };

  const renderStatus = () => {
    if (!isOwn) return null;

    const iconClass = 'size-3.5';

    switch (message.status) {
      case 'sent':
        return <Check className={cn(iconClass, 'text-muted-foreground/60')} />;
      case 'delivered':
        return <CheckCheck className={cn(iconClass, 'text-muted-foreground/60')} />;
      case 'read':
        return <CheckCheck className={cn(iconClass, 'text-green-500')} />;
      default:
        return null;
    }
  };

  return (
    <div
      className={cn(
        'flex flex-col my-1.5 mx-4 animate-in fade-in-0 slide-in-from-bottom-2 duration-200',
        isOwn ? 'items-end' : 'items-start'
      )}
    >
      <div
        className={cn(
          'max-w-[75%] px-4 py-2.5 rounded-2xl transition-shadow',
          message.type === 'image' ? 'p-1.5' : '',
          isOwn
            ? 'bg-primary text-primary-foreground rounded-br-md shadow-md shadow-primary/20'
            : 'bg-muted/80 border border-border/30 rounded-bl-md shadow-sm'
        )}
      >
        {!isOwn && (
          <span className="text-[11px] text-primary font-semibold block mb-1">
            {message.sender.name}
          </span>
        )}
        {renderContent()}
      </div>
      <div
        className={cn(
          'flex items-center gap-1.5 mt-1 px-2',
          isOwn ? 'flex-row-reverse' : 'flex-row'
        )}
      >
        <span className="text-[10px] text-muted-foreground/70 font-medium tabular-nums">
          {formatTime(message.createdAt)}
        </span>
        {renderStatus()}
      </div>
    </div>
  );
}
